import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {IoIosArrowBack, IoIosArrowForward} from "react-icons/io";
import {IoClose} from "react-icons/io5";
import {Swiper, SwiperSlide} from "swiper/react";
import {Zoom, Keyboard, Mousewheel} from "swiper/modules";
import type {Swiper as SwiperClass} from "swiper";
import "swiper/css";
import "swiper/css/zoom";

// Full-screen viewer: the photo lightbox, and the CV viewer (links.tsx).
interface LightboxProps {
    count: number;
    index: number;
    // what goes in slide i's zoom container: an <img className="lightbox-image">,
    // or any element with the `swiper-zoom-target` class. Clicks on it must not
    // bubble (a click on the backdrop closes the lightbox). Only called for
    // slides near the current one.
    renderSlide: (i: number) => ReactNode;
    slideKey: (i: number) => string;
    onClose: () => void;
    onNavigate?: (index: number) => void;
    // extra buttons, top right (give them the `lightbox-action` class)
    actions?: ReactNode;
    maxZoom?: number;
    // double-click/double-tap to zoom; off where double-click should select a word
    zoomToggle?: boolean;
}

const CLOSE_ANIM_MS = 300;
const DISMISS_THRESHOLD = 120;

type WebkitStyle = CSSStyleDeclaration & {webkitBackdropFilter: string};

const BACKDROP_TRANSITION = `background-color ${CLOSE_ANIM_MS}ms linear, backdrop-filter ${CLOSE_ANIM_MS}ms linear, -webkit-backdrop-filter ${CLOSE_ANIM_MS}ms linear`;
const BUTTON_TRANSITION = `background-color ${CLOSE_ANIM_MS}ms linear, backdrop-filter ${CLOSE_ANIM_MS}ms linear, -webkit-backdrop-filter ${CLOSE_ANIM_MS}ms linear, border-color ${CLOSE_ANIM_MS}ms linear, color ${CLOSE_ANIM_MS}ms linear, box-shadow ${CLOSE_ANIM_MS}ms linear`;

// The backdrop and buttons both have `backdrop-filter: blur(...)` which visually
// breaks when animated via opacity (the blur snaps out rather than fading). So
// we interpolate their actual paint properties — bg color, blur radius, border,
// text — instead of relying on opacity.
function setBackdropProgress(el: HTMLElement, p: number) {
    el.style.backgroundColor = `rgba(0, 0, 0, ${0.7 * p})`;
    const blur = `blur(${10 * p}px)`;
    el.style.backdropFilter = blur;
    (el.style as WebkitStyle).webkitBackdropFilter = blur;
}
function clearBackdropInline(el: HTMLElement) {
    el.style.backgroundColor = "";
    el.style.backdropFilter = "";
    (el.style as WebkitStyle).webkitBackdropFilter = "";
}
// A button's resting background as [r, g, b, alpha]; matches App.css: dark
// glass, or deep mint for a .primary action.
function buttonBg(el: HTMLElement): [number, number, number, number] {
    return el.classList.contains("primary") ? [24, 92, 74, 0.85] : [18, 26, 24, 0.72];
}
function setButtonProgress(el: HTMLElement, p: number) {
    const [r, g, b, a] = buttonBg(el);
    el.style.backgroundColor = `rgba(${r}, ${g}, ${b}, ${a * p})`;
    const blur = `blur(${10 * p}px)`;
    el.style.backdropFilter = blur;
    (el.style as WebkitStyle).webkitBackdropFilter = blur;
    el.style.borderColor = `rgba(255, 255, 255, ${0.18 * p})`;
    el.style.color = `rgba(255, 255, 255, ${0.8 * p})`;
    el.style.boxShadow = buttonShadow(p);
}
// A button's drop shadow at fade progress p (matches App.css).
function buttonShadow(p: number) {
    return `0 4px 16px rgba(0, 0, 0, ${0.3 * p})`;
}
function clearButtonInline(el: HTMLElement) {
    el.style.backgroundColor = "";
    el.style.backdropFilter = "";
    (el.style as WebkitStyle).webkitBackdropFilter = "";
    el.style.borderColor = "";
    el.style.color = "";
    el.style.boxShadow = "";
}
const CHROME_BUTTONS = ".lightbox-close, .lightbox-nav, .lightbox-action";
function getChromeButtons(chrome: HTMLElement) {
    return Array.from(chrome.querySelectorAll<HTMLElement>(CHROME_BUTTONS));
}
function getChromeCounter(chrome: HTMLElement) {
    return chrome.querySelector<HTMLElement>(".lightbox-counter");
}

const SLIDE_WINDOW = 2;

// After a trackpad swipe dismisses the lightbox, the trackpad keeps sending
// inertial wheel events; once the lightbox is gone they would scroll the page.
// Swallow wheel events until they pause for `gapMs` (the inertia has ended).
// Lives on window so it outlasts the lightbox unmounting.
function swallowWheelMomentum(gapMs: number) {
    let timer = window.setTimeout(stop, gapMs);
    function onWheel(e: WheelEvent) {
        e.preventDefault();
        window.clearTimeout(timer);
        timer = window.setTimeout(stop, gapMs);
    }
    function stop() {
        window.removeEventListener("wheel", onWheel, {capture: true});
    }
    window.addEventListener("wheel", onWheel, {passive: false, capture: true});
}

// text selected (CV viewer): taps and drags are for the selection, not for dismissing
function hasSelection() {
    return !(window.getSelection()?.isCollapsed ?? true);
}

export function Lightbox({count, index, renderSlide, slideKey, onClose, onNavigate, actions, maxZoom = 4, zoomToggle = true}: LightboxProps) {
    const [activeIdx, setActiveIdx] = useState(index);
    const swiperRef = useRef<SwiperClass | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const chromeRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const closingRef = useRef(false);
    const dragRef = useRef<{startX: number; startY: number; dy: number; active: boolean} | null>(null);

    // Unified close: content slides down + fades, backdrop and chrome buttons
    // unwind their paint properties (bg + blur + borders + text) in parallel,
    // then unmount via parent's onClose. Used by the X button, Escape, and
    // backdrop clicks. Swipe-to-dismiss has its own path in handleTouchEnd that
    // continues the existing drag motion.
    const beginClose = useCallback(() => {
        if (closingRef.current) return;
        closingRef.current = true;
        const content = contentRef.current;
        const chrome = chromeRef.current;
        const backdrop = backdropRef.current;
        // The open animations use `animation-fill-mode: forwards`, which keeps the
        // animated values "held" on the element even after the keyframes end. CSS
        // animation values outrank inline styles, so the close transitions below
        // won't take effect unless we cancel the held animation first.
        // Each part is pinned at its resting look and that's committed (the
        // offsetWidth reads) before its fade starts; dropping the animation and
        // setting the target in one go skips the fade, so the photo and
        // backdrop would vanish on the spot.
        if (content) {
            content.style.animation = "none";
            content.style.transition = "none";
            content.style.transform = "translateY(0)";
            content.style.opacity = "1";
            void content.offsetWidth;
            content.style.transition = `transform ${CLOSE_ANIM_MS}ms cubic-bezier(0.3, 0, 0.6, 1), opacity ${CLOSE_ANIM_MS}ms linear`;
            content.style.transform = "translateY(20px)";
            content.style.opacity = "0";
        }
        if (chrome) {
            getChromeButtons(chrome).forEach(b => {
                b.style.animation = "none";
                b.style.transition = BUTTON_TRANSITION;
                void b.offsetWidth; // commit before fading, or the fade is skipped
                setButtonProgress(b, 0);
            });
            const counter = getChromeCounter(chrome);
            if (counter) {
                counter.style.animation = "none";
                counter.style.transition = `opacity ${CLOSE_ANIM_MS}ms linear`;
                counter.style.opacity = "0";
            }
        }
        if (backdrop) {
            backdrop.style.animation = "none";
            backdrop.style.transition = "none";
            setBackdropProgress(backdrop, 1);
            void backdrop.offsetWidth;
            backdrop.style.transition = BACKDROP_TRANSITION;
            setBackdropProgress(backdrop, 0);
        }
        setTimeout(() => onCloseRef.current(), CLOSE_ANIM_MS);
    }, []);

    // Fade the buttons and counter out after a second of inactivity, and back in
    // on activity: cursor movement for a mouse/trackpad (not while resting on a
    // button), any touch on phones. Uses the same paint-property fade as
    // closing, so the blur stays intact. While hidden the buttons ignore taps,
    // so a tap where an arrow was just reveals them instead of changing photo.
    useEffect(() => {
        const IDLE_MS = 1000;
        let hidden = false;
        let timer: number | undefined;
        let wake: number | undefined;
        const buttons = () => chromeRef.current ? getChromeButtons(chromeRef.current) : [];
        const setVisible = (visible: boolean) => {
            if (closingRef.current || hidden === !visible) return;
            hidden = !visible;
            // re-enable taps only once the fade-in is done, i.e. after the
            // revealing tap has ended, so its release can't land on a button
            window.clearTimeout(wake);
            if (hidden) chromeRef.current?.classList.add("idle");
            else wake = window.setTimeout(() => chromeRef.current?.classList.remove("idle"), CLOSE_ANIM_MS);
            buttons().forEach(b => {
                b.style.animation = "none";
                b.style.transition = BUTTON_TRANSITION;
                void b.offsetWidth; // commit before fading, or the fade is skipped
                if (visible) clearButtonInline(b); else setButtonProgress(b, 0);
            });
            const counter = chromeRef.current && getChromeCounter(chromeRef.current);
            if (counter) {
                counter.style.animation = "none";
                counter.style.transition = `opacity ${CLOSE_ANIM_MS}ms linear`;
                counter.style.opacity = visible ? "" : "0";
            }
        };
        const schedule = (e?: PointerEvent) => {
            window.clearTimeout(timer);
            const overButton = e?.target instanceof Element && e.target.closest(CHROME_BUTTONS);
            if (!overButton) timer = window.setTimeout(() => setVisible(false), IDLE_MS);
        };
        const onMove = (e: PointerEvent) => {
            if (e.pointerType !== "mouse") return;
            setVisible(true);
            schedule(e);
        };
        const onTouch = (e: PointerEvent) => {
            if (e.pointerType === "mouse") return;
            setVisible(true);
            schedule();
        };
        window.addEventListener("pointermove", onMove);
        // capture: runs before the tap reaches anything, so hidden buttons are revealed, not pressed
        window.addEventListener("pointerdown", onTouch, true);
        // the open animation finishes before the first hide
        timer = window.setTimeout(() => setVisible(false), IDLE_MS);
        return () => {
            window.clearTimeout(timer);
            window.clearTimeout(wake);
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerdown", onTouch, true);
        };
    }, []);

    // Escape-to-close (Swiper's Keyboard module handles arrows)
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") beginClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [beginClose]);

    // Hide body content behind the lightbox (see App.css `body.lightbox-open`)
    useEffect(() => {
        document.body.classList.add("lightbox-open");
        return () => document.body.classList.remove("lightbox-open");
    }, []);

    // --- shared drag-to-dismiss visuals (used by touch and trackpad) ---

    // Cancel the open animations (`animation-fill-mode: forwards` keeps them
    // "held" after completion, which would block inline style updates below).
    const prepareDrag = () => {
        if (contentRef.current) {
            contentRef.current.style.animation = "none";
            contentRef.current.style.transition = "none";
        }
        if (backdropRef.current) {
            backdropRef.current.style.animation = "none";
            backdropRef.current.style.transition = "none";
        }
        if (chromeRef.current) {
            getChromeButtons(chromeRef.current).forEach(b => {
                b.style.animation = "none";
                b.style.transition = "none";
            });
            const counter = getChromeCounter(chromeRef.current);
            if (counter) {
                counter.style.animation = "none";
                counter.style.transition = "none";
            }
        }
    };

    // Move the photo to `dy` px (negative = pushed up) and fade everything by
    // how far down it is. Hits 0 opacity at half the viewport height.
    const dragTo = (dy: number) => {
        const opacity = Math.max(0, 1 - Math.max(0, dy) / (window.innerHeight / 2));
        if (contentRef.current) {
            contentRef.current.style.transform = `translateY(${dy}px)`;
            contentRef.current.style.opacity = String(opacity);
        }
        if (backdropRef.current) setBackdropProgress(backdropRef.current, opacity);
        if (chromeRef.current) {
            getChromeButtons(chromeRef.current).forEach(b => setButtonProgress(b, opacity));
            const counter = getChromeCounter(chromeRef.current);
            if (counter) counter.style.opacity = String(opacity);
        }
    };

    // Release a drag at `dy`: past the threshold it continues down and closes,
    // otherwise everything springs back to rest.
    const finishDrag = (dy: number) => {
        const content = contentRef.current;
        const chrome = chromeRef.current;
        const backdrop = backdropRef.current;
        if (content) {
            content.style.transition = `transform ${CLOSE_ANIM_MS}ms cubic-bezier(0.25, 1, 0.5, 1), opacity ${CLOSE_ANIM_MS}ms cubic-bezier(0.25, 1, 0.5, 1)`;
        }
        if (backdrop) backdrop.style.transition = BACKDROP_TRANSITION;
        if (chrome) {
            getChromeButtons(chrome).forEach(b => b.style.transition = BUTTON_TRANSITION);
            const counter = getChromeCounter(chrome);
            if (counter) counter.style.transition = `opacity ${CLOSE_ANIM_MS}ms linear`;
        }
        if (dy > DISMISS_THRESHOLD) {
            // Dismiss: continue the downward motion and unwind all paint properties together.
            closingRef.current = true;
            if (content) {
                content.style.transform = `translateY(${dy + 60}px)`;
                content.style.opacity = "0";
            }
            if (backdrop) setBackdropProgress(backdrop, 0);
            if (chrome) {
                getChromeButtons(chrome).forEach(b => setButtonProgress(b, 0));
                const counter = getChromeCounter(chrome);
                if (counter) counter.style.opacity = "0";
            }
            setTimeout(() => onCloseRef.current(), CLOSE_ANIM_MS);
        } else {
            // Snap back to rest.
            if (content) {
                content.style.transform = "";
                content.style.opacity = "";
            }
            if (backdrop) clearBackdropInline(backdrop);
            if (chrome) {
                getChromeButtons(chrome).forEach(b => clearButtonInline(b));
                const counter = getChromeCounter(chrome);
                if (counter) counter.style.opacity = "";
            }
        }
    };

    // --- touch ---

    const handleTouchStart = (e: React.TouchEvent) => {
        if (closingRef.current) return;
        if (e.touches.length !== 1) {
            dragRef.current = null;
            return;
        }
        // Don't dismiss-drag while zoomed in — Swiper's Zoom module owns the pan.
        if ((swiperRef.current?.zoom.scale ?? 1) > 1) return;
        // or while text is selected: the touch may be on a selection handle
        if (hasSelection()) return;
        const t = e.touches[0];
        dragRef.current = {startX: t.clientX, startY: t.clientY, dy: 0, active: false};
        prepareDrag();
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const drag = dragRef.current;
        if (!drag || e.touches.length !== 1 || closingRef.current) return;
        const t = e.touches[0];
        const dx = t.clientX - drag.startX;
        const dy = t.clientY - drag.startY;

        if (!drag.active) {
            // Decide direction: if the motion is dominantly horizontal or upward,
            // let Swiper handle it and stay out of the way.
            if (Math.abs(dy) < 10 && Math.abs(dx) < 10) return; // not enough signal
            if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
                dragRef.current = null;
                return;
            }
            drag.active = true;
            // Once committed, freeze Swiper so diagonal drags don't paginate underneath.
            if (swiperRef.current) swiperRef.current.allowTouchMove = false;
        }

        drag.dy = Math.max(0, dy);
        dragTo(drag.dy);
    };

    const handleTouchEnd = () => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (swiperRef.current) swiperRef.current.allowTouchMove = true;
        if (closingRef.current) return;
        if (!drag || !drag.active) {
            // Restore the CSS transitions prepareDrag switched off.
            if (contentRef.current) contentRef.current.style.transition = "";
            if (backdropRef.current) backdropRef.current.style.transition = "";
            if (chromeRef.current) {
                getChromeButtons(chromeRef.current).forEach(b => b.style.transition = "");
                const counter = getChromeCounter(chromeRef.current);
                if (counter) counter.style.transition = "";
            }
            return;
        }
        finishDrag(drag.dy);
    };

    // --- trackpad ---
    // Pinch zooms toward the pointer (Chrome/Firefox send it as ctrl+wheel,
    // Safari as gesture events). Not zoomed: a two-finger swipe down drags the
    // photo down to dismiss it (springing back if released short), swiping up
    // does nothing, and left/right is left to Swiper's Mousewheel.
    // Zoomed: two-finger swipes pan the photo.
    const lightboxRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const root = lightboxRef.current;
        if (!root) return;
        const MAX_ZOOM = maxZoom;
        const GESTURE_GAP_MS = 140; // a pause this long ends a trackpad gesture
        // ...but a dismiss-drag survives a longer pause, so the gap between
        // lifting the fingers and the trackpad's inertia doesn't end it
        const DRAG_GAP_MS = 250;
        const AXIS_SAMPLE_PX = 6;      // movement needed before picking a direction
        const HORIZONTAL_BIAS = 1.7;   // sideways must beat vertical by this to count as "next photo"
        const WHEEL_DISMISS_PX = 80;   // dismiss once dragged this far...
        const WHEEL_FLICK_PX = 40;     // ...or released past this while still moving down

        let axis: "x" | "y" | null = null;
        let sumX = 0, sumY = 0;
        let wheelDy = 0;
        let lastDy = 0;
        let dragging = false;
        let zoomed = false;
        let safariPinch = false;
        let pinchStartScale = 1;
        let idleTimer: number | undefined;

        const zoomEls = () => {
            const swiper = swiperRef.current;
            const slide = swiper?.slides[swiper.activeIndex] as HTMLElement | undefined;
            const wrap = slide?.querySelector<HTMLElement>(".swiper-zoom-container");
            const img = wrap?.querySelector<HTMLElement>(".swiper-zoom-target, img");
            return swiper && slide && wrap && img ? {swiper, slide, wrap, img} : null;
        };
        const readPan = (wrap: HTMLElement) => {
            const m = /translate3d\((-?[\d.]+)px,\s*(-?[\d.]+)px/.exec(wrap.style.transform);
            return m ? {x: parseFloat(m[1]), y: parseFloat(m[2])} : {x: 0, y: 0};
        };
        const setZoomed = (on: boolean) => {
            const swiper = swiperRef.current;
            if (!swiper || zoomed === on) return;
            zoomed = on;
            // while zoomed, horizontal swipes pan instead of changing photo
            if (on) swiper.mousewheel.disable(); else swiper.mousewheel.enable();
        };
        // Apply a zoom level and pan, keeping the photo covering its slide.
        const applyZoom = (scale: number, x: number, y: number) => {
            const els = zoomEls();
            if (!els) return;
            const {swiper, slide, wrap, img} = els;
            const maxX = Math.max(0, (img.offsetWidth * scale - slide.offsetWidth) / 2);
            const maxY = Math.max(0, (img.offsetHeight * scale - slide.offsetHeight) / 2);
            x = Math.min(maxX, Math.max(-maxX, x));
            y = Math.min(maxY, Math.max(-maxY, y));
            wrap.style.transitionDuration = "0ms";
            wrap.style.transform = `translate3d(${x}px, ${y}px, 0)`;
            img.style.transitionDuration = "0ms";
            img.style.transform = `translate3d(0,0,0) scale(${scale})`;
            swiper.zoom.scale = scale;
            setZoomed(scale > 1);
        };
        // Zoom to `next`, keeping the point under the pointer fixed.
        const zoomAt = (next: number, clientX: number, clientY: number) => {
            const els = zoomEls();
            if (!els) return;
            const {swiper, slide, wrap} = els;
            const scale = swiper.zoom.scale || 1;
            if (scale <= 1 && next > 1) {
                // let Swiper register the zoomed slide so double-click and
                // mouse-drag panning keep working; we take over from here
                swiper.zoom.in(1.0001);
            }
            next = Math.min(MAX_ZOOM, Math.max(1, next));
            const rect = slide.getBoundingClientRect();
            const px = clientX - (rect.left + rect.width / 2);
            const py = clientY - (rect.top + rect.height / 2);
            const {x, y} = readPan(wrap);
            applyZoom(next, px - (px - x) * next / scale, py - (py - y) * next / scale);
        };
        const endZoomIfOut = () => {
            const swiper = swiperRef.current;
            if (swiper && (swiper.zoom.scale || 1) <= 1.01) {
                swiper.zoom.out();
                setZoomed(false);
            }
        };

        const dismissFromWheel = () => {
            dragging = false;
            window.clearTimeout(idleTimer);
            finishDrag(Math.max(wheelDy, DISMISS_THRESHOLD + 1));
            swallowWheelMomentum(GESTURE_GAP_MS);
        };
        const endGesture = () => {
            axis = null;
            sumX = sumY = 0;
            if (dragging && !closingRef.current) {
                // released: a flick that was still heading down dismisses
                if (wheelDy > WHEEL_FLICK_PX && lastDy < 0) dismissFromWheel();
                else { dragging = false; finishDrag(0); }
            }
            dragging = false;
            wheelDy = 0;
            lastDy = 0;
            endZoomIfOut();
        };
        const bumpIdle = () => {
            window.clearTimeout(idleTimer);
            idleTimer = window.setTimeout(endGesture, dragging ? DRAG_GAP_MS : GESTURE_GAP_MS);
        };

        const onWheel = (e: WheelEvent) => {
            if (closingRef.current) return;
            // pixels, whatever the browser's delta mode
            const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
            const dx = e.deltaX * k, dy = e.deltaY * k;
            const swiper = swiperRef.current;
            // a double-click may have zoomed in or out behind our back
            if (swiper) setZoomed((swiper.zoom.scale || 1) > 1);

            if (e.ctrlKey) { // pinch (Chrome/Firefox)
                e.preventDefault(); // no page zoom
                if (safariPinch || dragging) return;
                zoomAt((swiper?.zoom.scale || 1) * Math.exp(-dy * 0.01), e.clientX, e.clientY);
                bumpIdle();
                return;
            }
            if (zoomed) { // pan
                e.preventDefault();
                const els = zoomEls();
                if (els) {
                    const {x, y} = readPan(els.wrap);
                    applyZoom(els.swiper.zoom.scale, x - dx, y - dy);
                }
                bumpIdle();
                return;
            }
            // claim every swipe: stops Chrome's back/forward swipe gesture
            e.preventDefault();
            bumpIdle();
            if (axis === null) {
                // sample a few px of movement before committing, favouring
                // vertical so a slightly diagonal swipe down still dismisses.
                // Swiper doesn't see the sampled events (this listener runs
                // in the capture phase, before Swiper's on its own element).
                sumX += dx;
                sumY += dy;
                e.stopPropagation();
                if (Math.abs(sumX) + Math.abs(sumY) < AXIS_SAMPLE_PX) return;
                axis = Math.abs(sumX) > Math.abs(sumY) * HORIZONTAL_BIAS ? "x" : "y";
                if (axis === "x") return;
                // carry the sampled movement into the drag
                handleVertical(sumY);
                return;
            }
            if (axis === "x") return; // let it through to Swiper: changes photo
            e.stopPropagation(); // vertical: keep Swiper out of it
            handleVertical(dy);
        };

        // natural scrolling: fingers moving down give negative deltaY.
        // Only downward drags do anything; swiping up is ignored.
        const handleVertical = (dy: number) => {
            if (dy !== 0) lastDy = dy;
            wheelDy = Math.max(0, wheelDy - dy);
            if (!dragging) {
                if (wheelDy === 0) return;
                dragging = true;
                prepareDrag();
                bumpIdle(); // switch to the longer drag gap
            }
            dragTo(wheelDy);
            if (wheelDy > WHEEL_DISMISS_PX) dismissFromWheel();
        };

        // Safari pinch
        type GestureEvent = UIEvent & {scale: number; clientX: number; clientY: number};
        const onGestureStart = (e: Event) => {
            e.preventDefault();
            safariPinch = true;
            pinchStartScale = swiperRef.current?.zoom.scale || 1;
        };
        const onGestureChange = (e: Event) => {
            e.preventDefault();
            const g = e as GestureEvent;
            if (!closingRef.current && !dragging) zoomAt(pinchStartScale * g.scale, g.clientX, g.clientY);
        };
        const onGestureEnd = (e: Event) => {
            e.preventDefault();
            safariPinch = false;
            endZoomIfOut();
        };

        // capture phase: runs before Swiper's own wheel listener
        root.addEventListener("wheel", onWheel, {passive: false, capture: true});
        root.addEventListener("gesturestart", onGestureStart);
        root.addEventListener("gesturechange", onGestureChange);
        root.addEventListener("gestureend", onGestureEnd);
        return () => {
            window.clearTimeout(idleTimer);
            root.removeEventListener("wheel", onWheel, {capture: true});
            root.removeEventListener("gesturestart", onGestureStart);
            root.removeEventListener("gesturechange", onGestureChange);
            root.removeEventListener("gestureend", onGestureEnd);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A drag that starts on the slide and ends on the backdrop (selecting text)
    // fires its click on the backdrop; that isn't a backdrop click.
    const downOnSlideRef = useRef(false);

    return (
        <div
            ref={lightboxRef}
            className="lightbox"
            onPointerDown={(e) => {
                downOnSlideRef.current = e.target instanceof Element && !!e.target.closest(".swiper-zoom-target, .lightbox-image");
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!downOnSlideRef.current) beginClose();
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
        >
            <div className="lightbox-backdrop" ref={backdropRef}/>
            <div className="lightbox-content" ref={contentRef}>
                <Swiper
                    modules={[Zoom, Keyboard, Mousewheel]}
                    initialSlide={index}
                    loop={count > 1}
                    spaceBetween={32}
                    zoom={{maxRatio: maxZoom, toggle: zoomToggle}}
                    keyboard={{enabled: true}}
                    mousewheel={{forceToAxis: true, sensitivity: 0.1, thresholdDelta: 20, thresholdTime: 200}}
                    onSwiper={(s: SwiperClass) => { swiperRef.current = s; }}
                    onSlideChange={(s: SwiperClass) => {
                        setActiveIdx(s.realIndex);
                        onNavigate?.(s.realIndex);
                        s.zoom.out();
                    }}
                    className="lightbox-swiper"
                >
                    {Array.from({length: count}, (_, i) => {
                        const dist = Math.min(Math.abs(i - activeIdx), count - Math.abs(i - activeIdx));
                        return (
                            <SwiperSlide key={slideKey(i)} zoom>
                                <div className="swiper-zoom-container">
                                    {dist <= SLIDE_WINDOW && renderSlide(i)}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
            <div className="lightbox-chrome" ref={chromeRef}>
                <button
                    className="lightbox-close"
                    aria-label="Close"
                    onClick={(e) => { e.stopPropagation(); beginClose(); }}
                >
                    <IoClose/>
                </button>
                {actions && <div className="lightbox-actions" onClick={(e) => e.stopPropagation()}>{actions}</div>}
                {count > 1 && <>
                    <button
                        className="lightbox-nav lightbox-prev"
                        aria-label="Previous"
                        onClick={(e) => { e.stopPropagation(); swiperRef.current?.slidePrev(); }}
                    >
                        <IoIosArrowBack/>
                    </button>
                    <button
                        className="lightbox-nav lightbox-next"
                        aria-label="Next"
                        onClick={(e) => { e.stopPropagation(); swiperRef.current?.slideNext(); }}
                    >
                        <IoIosArrowForward/>
                    </button>
                    <div className="lightbox-counter">
                        {index + 1} / {count}
                    </div>
                </>}
            </div>
        </div>
    );
}
